import { Link } from "@inertiajs/react"
import { BookOpen, Check, Shield } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { CourseCoverImage } from "@/components/CourseCoverImage"
import { CourseFilesList } from "@/components/CourseFilesList"

// The body of the member course page: cover, title, progress, lessons, files.
// Shared by the member page and the admin course Preview, so what an admin
// previews is exactly what a member sees (plan 2026-09-23-001).

function ProgressBar({ completed, total }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-600">
          {completed} of {total} lessons complete
        </span>
        <span className="font-medium text-zinc-900">{pct}%</span>
      </div>
      <div
        className="h-2.5 bg-zinc-100 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className="h-full bg-kf-red rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function LessonRow({ lesson, href, index }) {
  return (
    <Link
      href={href}
      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-200 bg-white hover:border-kf-red/30 hover:shadow-sm transition-all group"
    >
      {/* Lesson number or completion check */}
      <div
        className={`flex items-center justify-center h-8 w-8 rounded-full shrink-0 text-sm font-medium ${
          lesson.completed
            ? "bg-green-100 text-green-700"
            : "bg-zinc-100 text-zinc-500 group-hover:bg-kf-red/10 group-hover:text-kf-red"
        }`}
      >
        {lesson.completed ? (
          <Check className="h-4 w-4" />
        ) : (
          <span>{index + 1}</span>
        )}
      </div>

      {/* Lesson title */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-900 truncate">{lesson.title}</p>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 shrink-0">
        {lesson.coachOnly && (
          <Badge variant="outline" className="text-xs gap-1">
            <Shield className="h-3 w-3" />
            Coach
          </Badge>
        )}
        {lesson.completed && (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
            Complete
          </Badge>
        )}
      </div>
    </Link>
  )
}

export function CourseOverview({ course, lessons = [], progress = {}, lessonHref }) {
  return (
    <>
      {/* Course header */}
      <div className="space-y-4">
        <div className="aspect-[3/1] rounded-xl overflow-hidden bg-zinc-100">
          <CourseCoverImage
            url={course.coverImageUrl}
            alt={course.title}
            wrapperClassName="h-full w-full min-h-[8rem]"
            imgClassName="w-full h-full object-cover"
            fallbackIconClassName="h-12 w-12 text-zinc-300"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-zinc-900">{course.title}</h1>
            {course.category && (
              <Badge variant="secondary">{course.category.name}</Badge>
            )}
          </div>
          {course.description && (
            <p className="text-zinc-600">{course.description}</p>
          )}
        </div>

        {/* Progress */}
        <ProgressBar completed={progress.completed || 0} total={progress.total || 0} />
      </div>

      {/* Lesson list */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-zinc-900">Lessons</h2>
        {lessons.length > 0 ? (
          <div className="space-y-2">
            {lessons.map((lesson, index) => (
              <LessonRow
                key={lesson.id}
                lesson={lesson}
                href={lessonHref(lesson)}
                index={index}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500">
            <BookOpen className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
            <p className="text-sm">No lessons available yet.</p>
          </div>
        )}
      </div>

      {/* Course files */}
      {course.files?.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-zinc-900">Files & Resources</h2>
          <CourseFilesList files={course.files} />
        </div>
      )}
    </>
  )
}
